import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { IMainMenuViewOption } from '@rbi-ctg/menu';
import { brandFont } from 'components/layout/brand-font';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import MenuTileGrid from 'components/menu-tile-grid';
import StoreSelected from 'components/store-selected';
import { TabbedHeader } from 'components/tabbed-header';
import useEffectOnce from 'hooks/use-effect-once';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import useMediaQuery from 'hooks/use-media-query';
import { useTabbedMenuHeader } from 'hooks/use-tabbed-menu-header';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingState } from 'state/loading';
import { useMainMenuContext, useNewMainMenuContext } from 'state/main-menu';
import { useStoreContext } from 'state/store';
import { EventName, emitEvent } from 'utils/event-hub';

const Header = styled.h1`
  color: ${p => p.theme.token('text-default')};
  font: ${brandFont.hero};
  margin: auto;
  position: relative;
  text-align: center;

  ${Styles.desktop`
    margin-top: 4rem;
  `}
`;

// TODO: Figure out main padding for header
const NewMainMenu = () => {
  const { setLoading } = useLoadingState();
  const { getMainMenu } = useNewMainMenuContext();
  const { loading, data } = getMainMenu();
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const { store } = useStoreContext();
  const { menuId } = useFeatureMenu();
  const useEnableTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);

  const {
    activeTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const isMobile = useMediaQuery('mobile');
  const theme = useTheme();
  const storeId = store.number;

  useEffect(() => {
    setLoading(!data || loading);
  }, [loading, data, setLoading]);

  useEffect(() => {
    if (!menuId) {
      return;
    }

    getMainMenu(menuId, region, storeId);
  }, [getMainMenu, menuId, region, storeId]);

  return (
    <StoreSelected>
      <Helmet title={formatMessage({ id: 'mainMenu' })} />
      {useEnableTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile ? (
        <TabbedHeader
          data={tabbedHeaderItems}
          loading={loading || isTabbedMenuLoading}
          activeTab={activeTab}
        />
      ) : (
        <Header>{data && data.headerText?.locale}</Header>
      )}
      {loading && (
        <LoadingContainer>
          <LoadingAnimation fillColor={theme.token('icon-active')} />
        </LoadingContainer>
      )}
      {data && data.options && <MenuTileGrid data={data.options as IMainMenuViewOption[]} />}
    </StoreSelected>
  );
};

const LegacyMainMenu = () => {
  const { setLoading } = useLoadingState();
  const { getMainMenu } = useMainMenuContext();
  const { loading, data } = getMainMenu();
  const { formatMessage } = useIntl();
  const useEnableTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);

  const {
    activeTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const isMobile = useMediaQuery('mobile');
  const theme = useTheme();

  useEffect(() => {
    setLoading(!data || loading);
  }, [loading, data, setLoading]);

  return (
    <StoreSelected>
      <Helmet title={formatMessage({ id: 'mainMenu' })} />
      {useEnableTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile ? (
        <TabbedHeader
          data={tabbedHeaderItems}
          loading={loading || isTabbedMenuLoading}
          activeTab={activeTab}
        />
      ) : (
        <Header>{data && data.headerText?.locale}</Header>
      )}
      {loading && (
        <LoadingContainer>
          <LoadingAnimation fillColor={theme.token('icon-active')} />
        </LoadingContainer>
      )}
      {data && data.options && <MenuTileGrid data={data.options as IMainMenuViewOption[]} />}
    </StoreSelected>
  );
};

const MainMenu = () => {
  const enableMenuServiceData = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);

  useEffectOnce(() => {
    emitEvent(EventName.SCREEN_RENDER, {
      screenId: 'menu',
    });
  });

  if (enableMenuServiceData) {
    return <NewMainMenu />;
  }

  return <LegacyMainMenu />;
};

export default MainMenu;
